'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/StatusBadge';
import { Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const estimates = [
  { id: 'ep-001', customer: 'Chen, B.', address: '4821 Barranca Pkwy, Irvine', system: 'Lennox XC21 5-ton', value: '$22,100', probability: 88, lastContact: '6h ago', nextFollowUp: 'Jul 2, 9AM', stage: 'Seq 3/5', status: 'Hot Lead', age: 4 },
  { id: 'ep-002', customer: 'Park, L.', address: '18 Shady Canyon, Irvine', system: 'Carrier Infinity 4-ton', value: '$16,800', probability: 74, lastContact: '1d ago', nextFollowUp: 'Today 4PM', stage: 'Seq 2/5', status: 'Warm', age: 8 },
  { id: 'ep-003', customer: 'Johnson, R.', address: '2244 Newport Blvd, CM', system: 'Trane XV20i 3-ton', value: '$14,200', probability: 61, lastContact: '2d ago', nextFollowUp: 'Jul 2, 2PM', stage: 'Seq 4/5', status: 'Warm', age: 12 },
  { id: 'ep-004', customer: 'Smith, A.', address: '9901 Alton Pkwy, Irvine', system: 'York YZV 5-ton', value: '$19,400', probability: 45, lastContact: '3d ago', nextFollowUp: 'Jul 3, 10AM', stage: 'Seq 2/5', status: 'Cooling', age: 6 },
  { id: 'ep-005', customer: 'Garcia, M.', address: '1122 Edinger Ave, Tustin', system: 'Rheem Prestige 4-ton', value: '$11,800', probability: 38, lastContact: '5d ago', nextFollowUp: 'Jul 4, 11AM', stage: 'Seq 5/5', status: 'At Risk', age: 18 },
  { id: 'ep-006', customer: 'Thompson, W.', address: '55 Monarch Beach, Dana Pt', system: 'Carrier 24ACC636 3-ton', value: '$13,400', probability: 82, lastContact: '4h ago', nextFollowUp: 'Today 6PM', stage: 'Seq 1/5', status: 'Hot Lead', age: 2 },
  { id: 'ep-007', customer: 'Lee, J.', address: '7700 Beach Blvd, Buena Park', system: 'Lennox SL18XC1 5-ton', value: '$18,900', probability: 55, lastContact: '2d ago', nextFollowUp: 'Jul 3, 1PM', stage: 'Seq 3/5', status: 'Warm', age: 9 },
  { id: 'ep-008', customer: 'Wilson, C.', address: '3300 Michelson Dr, Irvine', system: 'Trane XR15 4-ton', value: '$9,800', probability: 29, lastContact: '7d ago', nextFollowUp: 'Jul 5, 9AM', stage: 'Seq 5/5', status: 'Cold', age: 24 },
  { id: 'ep-009', customer: 'Martinez, L.', address: '8811 Garden Grove Blvd', system: 'York YZV 3-ton', value: '$12,400', probability: 67, lastContact: '1d ago', nextFollowUp: 'Jul 2, 11AM', stage: 'Seq 2/5', status: 'Warm', age: 5 },
  { id: 'ep-010', customer: 'Anderson, P.', address: '400 Columbia, Aliso Viejo', system: 'Carrier 24ACC648 5-ton', value: '$21,800', probability: 71, lastContact: '8h ago', nextFollowUp: 'Jul 2, 3PM', stage: 'Seq 1/5', status: 'Hot Lead', age: 3 },
];

type SortKey = 'value' | 'probability' | 'age';
type SortDir = 'asc' | 'desc';

const statusVariant: Record<string, 'active' | 'warning' | 'error' | 'idle' | 'pending'> = {
  'Hot Lead': 'active',
  'Warm': 'pending',
  'Cooling': 'warning',
  'At Risk': 'error',
  'Cold': 'idle',
};

export default function EstimatePipelineTable() {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('probability');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filter, setFilter] = useState('All');

  const statuses = ['All', 'Hot Lead', 'Warm', 'Cooling', 'At Risk', 'Cold'];

  const filtered = estimates
    .filter(e =>
      (filter === 'All' || e.status === filter) &&
      (e.customer.toLowerCase().includes(search.toLowerCase()) ||
       e.system.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      let av: number, bv: number;
      if (sortKey === 'value') {
        av = parseFloat(a.value.replace(/[$,]/g, ''));
        bv = parseFloat(b.value.replace(/[$,]/g, ''));
      } else if (sortKey === 'probability') {
        av = a.probability; bv = b.probability;
      } else {
        av = a.age; bv = b.age;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k
      ? (sortDir === 'desc' ? <ChevronDown size={12} className="text-secondary" /> : <ChevronUp size={12} className="text-secondary" />)
      : <ChevronDown size={12} className="text-muted-foreground/40" />;

  return (
    <div className="bg-card rounded-lg card-shadow border border-border">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Estimate Recovery Pipeline</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} estimates · $1.82M total pipeline</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer or system..."
              className="pl-8 pr-3 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring w-48"
            />
          </div>
          <div className="flex items-center gap-1">
            <Filter size={12} className="text-muted-foreground" />
            {statuses.map(s => (
              <button
                key={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-xs px-2 py-1 rounded-md transition-colors ${filter === s ? 'bg-secondary text-white' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Customer</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">System</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => toggleSort('value')}>
                <div className="flex items-center justify-end gap-1">Value <SortIcon k="value" /></div>
              </th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => toggleSort('probability')}>
                <div className="flex items-center justify-end gap-1">Prob% <SortIcon k="probability" /></div>
              </th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Last Touch</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Next Follow-up</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Stage</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Status</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => toggleSort('age')}>
                <div className="flex items-center justify-end gap-1">Age (d) <SortIcon k="age" /></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-foreground">{e.customer}</div>
                  <div className="text-muted-foreground text-xs mt-0.5 truncate max-w-[140px]">{e.address}</div>
                </td>
                <td className="px-3 py-2.5 text-foreground whitespace-nowrap">{e.system}</td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular text-foreground">{e.value}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className={`font-semibold font-tabular ${e.probability >= 70 ? 'text-success' : e.probability >= 50 ? 'text-warning' : 'text-danger'}`}>{e.probability}%</span>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.lastContact}</td>
                <td className="px-3 py-2.5 text-foreground">{e.nextFollowUp}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.stage}</td>
                <td className="px-3 py-2.5">
                  <StatusBadge variant={statusVariant[e.status] ?? 'idle'} label={e.status} />
                </td>
                <td className="px-3 py-2.5 text-right font-tabular text-muted-foreground">{e.age}d</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Showing {filtered.length} of {estimates.length} estimates</span>
        <span className="font-medium text-success">$38,420 recovered today from this pipeline</span>
      </div>
    </div>
  );
}